import { Injectable, inject } from '@angular/core';
import { CoreHttpClient } from '@bepatient/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Category, Event } from './events.models';

@Injectable()
export class EventsApiService {
  private http = inject(CoreHttpClient);

  loadEvents(): Observable<Array<Event>> {
    return this.http
      .get<any>('/api/patient-events/events', {
        withMobiCredentials: true
      })
      .pipe(map<any, Array<Event>>(res => this.eventsParser(res)));
  }

  loadCategories(): Observable<Array<Category>> {
    return this.http
      .get<any>('/api/patient-events/categories', {
        withMobiCredentials: true
      })
      .pipe(map<any, Array<Category>>(res => this.categoriesParser(res)));
  }

  saveEvent(
    categoryUuid: string,
    datetime: string,
    value: string,
    reminder: string
  ): Observable<any> {
    return this.http.post<any>(
      `/api/patient-events/categories/${categoryUuid}`,
      {
        datetime,
        value,
        reminder
      },
      {
        withMobiCredentials: true
      }
    );
  }

  deleteEvent(eventUuid: string): Observable<any> {
    return this.http.delete<any>(`/api/patient-events/events/${eventUuid}`, {
      withMobiCredentials: true
    });
  }

  updateEvent(
    eventUuid: string,
    datetime: string,
    reminder: string,
    value: string,
    attended: boolean
  ): Observable<any> {
    return this.http.patch<any>(
      `/api/patient-events/events/${eventUuid}`,
      {
        datetime,
        reminder,
        attended,
        value
      },
      {
        withMobiCredentials: true
      }
    );
  }

  private eventsParser(data: any): Array<Event> {
    const events = data.items || [];
    return events.map(
      item =>
        <Event>{
          uuid: item.uuid || '',
          categoryUuid: (item.meta && item.meta.category) || '',
          done:
            item.meta && typeof item.meta.attended === 'boolean'
              ? item.meta.attended
              : void 0,
          date: (item.meta && item.meta.datetime) || '',
          color: (item.meta && item.meta.category_color) || '',
          label: (item.meta && item.meta.title) || '',
          reminder: (item.meta && item.meta.reminder) || void 0,
          value: (item.meta && item.meta.answer && item.meta.answer.name) || ''
        }
    );
  }

  private categoriesParser(data: any): Array<Category> {
    return (data.items || []).map(item => {
      const reminderParam = (
        (item.links &&
          item.links.create_event &&
          item.links.create_event.parameters) ||
        []
      ).filter(param => param.name === 'reminder');
      return <Category>{
        uuid: item.uuid || '',
        color: (item.meta && item.meta.color) || '',
        label: (item.meta && item.meta.title) || '',
        descriptionTitle: (item.meta && item.meta.description_title) || '',
        descriptionText: (item.meta && item.meta.description_text) || '',
        withReminder: item.meta && item.meta.with_reminder,
        withAttendance: item.meta && item.meta.with_attendance,
        question:
          item.meta && item.meta.question
            ? {
                withQuestion: item.meta.question.with_question || false,
                options: item.meta.question.options || [],
                title: item.meta.question.title || ''
              }
            : {},
        reminders:
          (reminderParam.length &&
            (reminderParam[0].options || []).map(option => ({
              name: option.value,
              value: option.name
            }))) ||
          []
      };
    });
  }
}
